import React, { useState } from 'react';

import { LoadingButton } from '@mui/lab';
import * as dateFns from 'date-fns';
import _ from 'lodash';
import {
  Fab,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  OutlinedInput,
  InputAdornment,
  TextField,
  MenuItem,
  InputLabel,
  Select,
  Autocomplete,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { NumericFormatCustom } from 'src/sections/scenarios/NumericFormatCustom';
import MonthYearSelector from 'src/components/plaid/MonthYearSelector';
import { useFinanceContext } from 'src/components/plaid/datasource';
import { categories } from 'src/utils/catmap';
import { saveBudgetPatches } from 'src/components/plaid/budget';
import { timestamp } from 'src/utils/firebase';
import { endOfRollupPeriod, segmentKeyForDate } from 'src/utils/rollup';

export default function QuickBudgetButton() {
  const {
    userId,
    dateRange,
    setDateRange,
    patches,
    rollup: period,
    expenses: { aggregateData: expenses = {} } = {},
    income: { aggregateData: income = {} } = {},
  } = useFinanceContext();

  const [amount, setAmount] = useState(null);
  const [category, setCategory] = useState(null);
  const [type, setType] = useState('onetime');
  const [growth, setGrowth] = useState(false);
  const [memo, setMemo] = useState('');

  const isRecurring = type === 'recurring';
  const options = _.uniqBy(categories, 'detailed');
  const [date, setDate] = useState(new Date());

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const onClose = () => setOpen(false);

  // get the forecasted value for the selected date
  const budget_placeholder_value = React.useMemo(() => {
    const { startDate: start, endDate: end } = dateRange;
    const interval = { start, end };

    if (!date || !(interval?.start && interval?.end)) return 0;
    const key = segmentKeyForDate(date, period, 1);
    const dataset = { ...income, ...expenses };

    const isSegmentWithinForecast = dateFns.isWithinInterval(date, interval);
    if (!isSegmentWithinForecast)
      setDateRange({ startDate: start, endDate: endOfRollupPeriod(date, period) });

    return dataset[category?.key]?.data?.[key];
  }, [period, category, dateRange, date, income, expenses]);

  const handleAdd = () => {
    const row = {
      amount,
      type,
      growth,
      memo,
    };

    const startTimestamp = timestamp(dateFns.startOfMonth(date));

    // Add budget patch to any existing scenario based budget patches
    const existingPatch = patches?.find(
      (e) =>
        e.timestamp?.toDate()?.getTime() === startTimestamp?.toDate()?.getTime() &&
        e.scenario === null
    );

    const newPatch = existingPatch ?? {
      user_id: userId,
      timestamp: startTimestamp,
      categories: {},
    };

    newPatch.categories[category?.key] = row;

    setLoading(true);
    saveBudgetPatches([newPatch]).then(() => {
      setLoading(false);
      setOpen(false);
    });
  };

  const isValid = amount && category?.key;

  const renderDialog = () => (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Edit Budget</DialogTitle>
      <DialogContent>
        <Stack spacing={1} sx={{ width: '100%', p: 1 }}>
          <>
            <MonthYearSelector value={date} onChange={setDate} />
            <Autocomplete
              id="category-select"
              options={options}
              sx={{ width: '100%' }}
              value={category?.id}
              onChange={(e, value) => setCategory(value)}
              getOptionLabel={(option) => option.detailed}
              renderInput={(params) => <TextField {...params} label="Detailed Category" />}
              size="small"
              fullWidth
            />
            <Stack direction="row" spacing={1}>
              <FormControl fullWidth size="small">
                <OutlinedInput
                  id="amount"
                  placeholder={String(budget_placeholder_value?.toFixed(2) ?? 'Amount')}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value ? Number(e.target.value) : null)}
                  inputComponent={NumericFormatCustom}
                  startAdornment={<InputAdornment position="start">$</InputAdornment>}
                  endAdornment={isRecurring && <InputAdornment position="end">/ mo</InputAdornment>}
                  inputProps={{
                    'aria-label': `Amount`,
                  }}
                />
              </FormControl>

              <FormControl fullWidth size="small" sx={{ maxWidth: 125 }}>
                <InputLabel id="type-label">Type</InputLabel>
                <Select
                  labelId="type-label"
                  id="type"
                  value={type}
                  label="Type"
                  onChange={(e, v) => setType(e.target.value)}
                  size="small"
                >
                  <MenuItem value="onetime">One Time</MenuItem>
                  <MenuItem value="recurring">Recurring</MenuItem>
                </Select>
              </FormControl>
              {isRecurring && (
                <FormControl sx={{ minWidth: 100 }} size="small">
                  <InputLabel id="growth-label">Growth</InputLabel>
                  <Select
                    labelId="growth-label"
                    id="growth"
                    value={growth}
                    label="Growth"
                    onChange={(e, v) => setGrowth(e.target.value)}
                    size="small"
                  >
                    <MenuItem value={false}>None</MenuItem>
                    <MenuItem value="month">Monthly</MenuItem>
                    <MenuItem value="quarter">Quarterly</MenuItem>
                    <MenuItem value="year">Yearly</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Stack>
            <TextField
              multiline
              label="Notes"
              placeholder="Add a memo to this budget adjustment"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              fullWidth
              rows={1}
              size="small"
              variant="filled"
            />
          </>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <LoadingButton
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleAdd}
          disabled={!isValid}
          loading={loading}
        >
          Create Budget Change
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      {renderDialog()}
      <Fab
        sx={{
          position: 'fixed',
          left: 'auto',
          top: 'auto',
          bottom: 16,
          right: 16,
        }}
        variant="extended"
        color="primary"
        onClick={() => setOpen(true)}
      >
        <Edit sx={{ mr: 1 }} />
        Budget
      </Fab>
    </>
  );
}
